import Chart from "@common/controls/Chart/component";
import { type ChartConfig, type ChartType } from "@common/controls/Chart/types";
import FullHeightContainer from "@common/controls/Container/FullHeight";
import { type DynamicFormMapOptions, mapFieldValue } from "@common/controls/Form/DynamicForm/lib";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { type OptionItem } from "@common/controls/Inputs/Select/component";
import { DynamicFormModal } from "@common/controls/Modal/Modals/Form/component";
import { type Api, type GenericMap } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { useNavigation } from "@common/hooks/navigation";
import { sort } from "@common/lib/array";
import { useUpdated } from "@common/lib/hooks";
import { afterSuccess, EMPTY_OBJECT } from "@common/lib/misc";
import { type Data } from "@common/modules/data/types";
import { getApi as getNetworkApi } from "@common/modules/network/api";
import { Common } from "@common/types";
import React from "react";
import Badges from "./Badges";
import Breadcrumbs from "./Breadcrumbs";
import DiagramReport, { type DiagramConfig } from "./Diagram/component";
import Grid from "./Grid/component";
import { expandReportParameters, getFinalReportParameters } from "./lib";
import {
  type ExtraGridConfigColumn,
  type GridConfig,
  type ReportData,
  type ReportGetItemDetail,
  type ReportSettings,
  type ReportTitle
} from "./types";

import "./style.less";

export type ReportProps = ReportData & {
  alwaysShowRunButton: boolean;
  extraColumns?: ExtraGridConfigColumn<any>[];
  fieldStyle?: React.CSSProperties;
  id: string;
  getItemDetail?: ReportGetItemDetail;
  noRun?: boolean;
  onChange?: (reportId: string) => void;
  options?: OptionItem<string>[];
  rootId: string | null;
  settings?: ReportSettings;
  title?: ReportTitle;
  useUrlState: boolean;
};

export type IReport = {
  fields: Common.System.FormField[];
  chartTypeSettings?: { types: ChartType[]; defaultValue: ChartType; onChange: (data: ChartType) => void };
  openFilters: () => void;
  name: string;
};

type ReportFormField = Common.System.FormField & {
  isHidden?: boolean;
};

const Report = React.forwardRef((props: ReportProps, ref: React.RefObject<IReport>) => {
  const i18n = useI18n();

  const networkApi = React.useMemo(() => getNetworkApi(i18n), [i18n]);

  const [data, setData] = React.useState<object[]>([]);
  const [isRunningReport, setIsRunningReport] = React.useState(false);
  const [parameters, setParameters] = React.useState<GenericMap<any>>({});
  const [finalParameters, setFinalParameters] = React.useState<GenericMap<any>>(EMPTY_OBJECT);
  const [filters, setFilters] = React.useState<Data.Filter[]>([]);
  const [visibleModal, setVisibleModal] = React.useState<"filters" | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [chartType, setChartType] = React.useState<ChartType>();
  const navigation = useNavigation();

  useUpdated(() => {
    props.isLoading && setData([]);
  }, [props.isLoading]);

  const fields = React.useMemo<ReportFormField[]>(() => {
    const fields = sort(props.fields, { key: "sortOrder" });
    const defaultParameters = fields.reduce(
      (res, x) => ({
        ...res,
        [x.formFieldKey!]: mapFieldValue(x.plainTextValue, x.formControlKey, x.dataType)
      }),
      {}
    );

    return fields.map((x) => {
      const value =
        props.settings && x.formFieldKey! in props.settings.customs
          ? props.settings.customs[x.formFieldKey!]
          : (props.parameters[x.formFieldKey!] ?? defaultParameters[x.formFieldKey!]) || null;

      const isVisibleBySettings = !props.settings?.visible || props.settings.visible.includes(x.formFieldKey!);
      return {
        ...x,
        isHidden: !isVisibleBySettings,
        plainTextValue: value,
        value
      };
    });
  }, [props.parameters, props.fields, props.settings]);

  const useOnlyGridData = React.useMemo(
    () => props.getGridData === props.getChartData || (!props.getGridData && props.hasGrid),
    [props.getGridData, props.getChartData, props.gridConfig]
  );

  const getChartData = React.useCallback(
    (args: Api.LoadPageArgs<any>, parameters?: GenericMap<any>) => {
      if (useOnlyGridData) return Promise.resolve([]);

      return props.getChartData(props.id, {
        ...(parameters ?? finalParameters),
        Page: args.page,
        Size: args.size,
        Search: args.search,
        // RecordsPerPage, CurrentPage, SearchPhrase will be removed in future
        RecordsPerPage: args.size,
        CurrentPage: args.page,
        SearchPhrase: args.search,
        SortDirection: args.order,
        SortColumn: args.sortBy
      });
    },
    [props.id, finalParameters, props.getChartData, useOnlyGridData]
  );

  const drillDownParameters = React.useMemo(
    () => (props.drillDownInfo ?? []).reduce((res, x) => ({ ...res, [x.key]: x.value }), {}),
    [props.drillDownInfo]
  );

  const getGridData = React.useCallback(
    async (args: Api.LoadPageArgs<any>, parameters?: GenericMap<any>) => {
      const func = props.hasGrid && props.getGridData ? props.getGridData : props.getChartData;
      const res = await func(props.id, {
        ...(parameters ?? finalParameters),
        ...(drillDownParameters ?? {}),
        Page: args.page,
        Size: args.size,
        Search: args.search,
        // RecordsPerPage, CurrentPage, SearchPhrase will be removed in future
        RecordsPerPage: args.size,
        CurrentPage: args.page,
        SearchPhrase: args.search
      });

      if (useOnlyGridData) {
        setData(res);
      }

      return res;
    },
    [props.id, props.hasGrid, drillDownParameters, finalParameters, props.getChartData, useOnlyGridData]
  );

  const onRunReport = React.useCallback(
    async (parameters: GenericMap<any>) => {
      setIsRunningReport(true);
      try {
        setParameters(parameters);

        const expandedParameters = expandReportParameters(fields, parameters);
        if (props.useUrlState) {
          navigation.setQuery({
            reportId: props.id,
            ...expandedParameters,
            ...(drillDownParameters ?? {})
          });
        }

        // NOTE: get final values, then expand, otherwise there can be no matching fields
        const finalParameters = expandReportParameters(fields, {
          ...getFinalReportParameters(fields, parameters),
          ...(drillDownParameters ?? {})
        });

        setFinalParameters(finalParameters);

        const [data, filters] = await Promise.all([
          getChartData({ page: 1, size: props.config.totalRecords ?? 1000000 }, finalParameters),
          props.getFilters(
            props.rootId ?? props.id,
            fields
              // dynamic source only
              .filter((x) => x.formFieldKey! in expandedParameters && x.source?.includes("api:"))
              .map((x) => ({
                formFieldId: x.formFieldId,
                value: expandedParameters[x.formFieldKey!]
              }))
          )
        ]);

        setData(data);
        setFilters(filters);
      } finally {
        setIsRunningReport(false);
        setIsLoading(false);
      }
    },
    [
      fields,
      getChartData,
      props.config.totalRecords,
      drillDownParameters,
      props.getFilters,
      props.id,
      props.rootId,
      props.useUrlState,
      setFilters,
      setData,
      setFinalParameters,
      setParameters
    ]
  );

  const mapOptions = React.useMemo(
    (): DynamicFormMapOptions => ({
      multiSelectOutput: "string"
    }),
    []
  );

  React.useEffect(() => {
    onRunReport(fields.reduce((res, x) => ({ ...res, [x.formFieldKey!]: x.value }), {}));
  }, [fields]);

  const onRowClick = React.useCallback(
    (item: any) => {
      props.onDrillDown!(item[props.config.drillDownKey!], "", parameters);
    },
    [props.onDrillDown, props.config.drillDownKey, parameters]
  );

  React.useImperativeHandle(
    ref,
    (): IReport => ({
      fields,
      chartTypeSettings: props.chartTypes
        ? {
            types: props.chartTypes.map((x) => x.type),
            defaultValue: props.type as ChartType,
            onChange: (type) => {
              setChartType(type);
            }
          }
        : undefined,
      openFilters: () => setVisibleModal("filters"),
      name: props.name
    }),
    [fields, props.name, props.chartTypes, props.type]
  );

  const onBadgeClick = React.useCallback(() => setVisibleModal("filters"), [setVisibleModal]);

  const onDrillDown = React.useCallback(
    (value: any, label: string) => {
      props.onDrillDown && props.onDrillDown(value, label, parameters);
    },
    [props.onDrillDown, parameters]
  );

  const formFields = React.useMemo(
    () =>
      fields.map<DynamicForms.Field>((x) => {
        const f = filters.find((y) => y.key === x.formFieldKey);
        // pull display label for ID values
        return {
          ...x,
          name: x.formFieldKey!,
          valuePlain: f?.displayLabel ?? f?.displayName,
          value: x.value ?? null
        };
      }),
    [fields, filters]
  );

  const getItemDetail = React.useMemo(
    (): ReportGetItemDetail =>
      props.getItemDetail ?? {
        genealogy: (item) => networkApi.getGenealogyDetail(item.id, parameters["CycleEndDate"])
      },
    [props.getItemDetail, parameters["CycleEndDate"]]
  );

  const config = React.useMemo((): DiagramConfig | ChartConfig => {
    let chartConfig = { ...props.config };

    if (props.chartTypes && chartType && "xAxis" in props.config) {
      const type = props.chartTypes.find((x) => x.type === chartType);
      chartConfig = {
        ...chartConfig,
        xAxis: {
          ...props.config.xAxis,
          key: type?.key ?? props.config.xAxis.key,
          stepSize: type?.stepSize
        }
      };
    }

    if ("yAxis" in props.config && !Array.isArray(props.config.yAxis)) {
      const yAxis = props.config.yAxis;
      return {
        ...chartConfig,
        yAxis: yAxis.get(data).map((x) => ({
          key: x.value,
          label: x.label,
          isStacked: yAxis.isStacked
        }))
      };
    }

    return chartConfig;
  }, [props.config, data, chartType, props.chartTypes]);

  const gridConfig = React.useMemo((): GridConfig | undefined => {
    if (!props.gridConfig) return;

    if (props.gridColumns) {
      return {
        ...props.gridConfig,
        columns: props.gridColumns(data)
      };
    }

    return props.gridConfig;
  }, [props.gridConfig, data, props.gridColumns]);

  return (
    <div className="report_container">
      {!props.isChartHidden && (
        <>
          {!isLoading && <Badges badges={props.getBadges(parameters, filters, onBadgeClick)} />}

          {props.type === "Diagram" ? (
            <FullHeightContainer className="diagram_container">
              <DiagramReport
                data={data}
                parameters={parameters}
                config={config as DiagramConfig}
                getItemDetail={getItemDetail}
              />
            </FullHeightContainer>
          ) : (
            <Chart
              config={config as ChartConfig}
              data={data}
              onDrillDown={props.onDrillDown ? onDrillDown : undefined}
              type={chartType ?? props.type}
            />
          )}
        </>
      )}

      {!!props.breadcrumbs?.length && !gridConfig && (
        <Breadcrumbs breadcrumbs={props.breadcrumbs} goTo={props.goTo} name={props.name} />
      )}

      {!!gridConfig && (props.settings?.includeGrid ?? true) && finalParameters !== EMPTY_OBJECT && (
        <Grid
          breadcrumbs={
            props.breadcrumbs?.length ? (
              <Breadcrumbs breadcrumbs={props.breadcrumbs} goTo={props.goTo} name={props.name} />
            ) : undefined
          }
          extraColumns={props.extraColumns}
          fieldStyle={props.fieldStyle}
          drillDownParameters={drillDownParameters}
          getData={getGridData}
          name={props.name}
          idKey={gridConfig.key ?? props.config["xAxis"]?.key}
          config={gridConfig}
          parameters={finalParameters}
          onRowClick={props.onDrillDown ? onRowClick : undefined}
        />
      )}

      <DynamicFormModal
        defaultValues={parameters}
        fields={formFields}
        isSubmitNoFieldsVisible={props.alwaysShowRunButton}
        onClose={() => setVisibleModal(null)}
        isSubmitButtonDisabled={isRunningReport}
        isVisible={visibleModal === "filters"}
        mapOptions={mapOptions}
        submit={props.noRun ? undefined : afterSuccess(onRunReport, () => setVisibleModal(null))}
        submitButtonCaption={i18n.applyFilters}
        title={i18n.applyFilters}
      />
    </div>
  );
});

export default Report;
